import { useCallback, useRef, useState } from 'react';
import { AgenciesService } from '@services/requests/housien-services/AgenciesService';
import type { ActivateNowResponse } from '@services/requests/housien-services/AgenciesService.subscription';
import { stateFromResponse, type ActivateState } from '../components/plan-v2/activate-state';

const CONFIRM: ActivateState = { phase: 'confirm' };

/** The `result`-carrying body of a rejected call, or undefined when it has none. */
function bodyOf(error: unknown): ActivateNowResponse | undefined {
  const body = (error as { body?: ActivateNowResponse } | null)?.body;
  return body && typeof body === 'object' ? body : undefined;
}

export interface UseActivateNowResult {
  state: ActivateState;
  /** Charges the plan today. Resolves to the new state; a second call while one runs is ignored. */
  activate: () => Promise<ActivateState | null>;
  reset: () => void;
}

/**
 * Ends the free trial and pays now (RN-10).
 *
 * A ref, not the `processing` state, is what stops a double click (AR-05):
 * state only updates on the next render, and two clicks can land in between.
 * Every outcome, including the non-200 ones the request layer throws, becomes
 * a state; nothing here reloads anything, the caller decides that.
 *
 * @param agencyId - The agency identifier.
 * @returns The dialog state, the action and a way back to the confirmation.
 */
export function useActivateNow(agencyId: string): UseActivateNowResult {
  const [state, setState] = useState<ActivateState>(CONFIRM);
  const inFlight = useRef(false);

  const activate = useCallback(async () => {
    if (inFlight.current) return null;
    inFlight.current = true;
    setState({ phase: 'processing' });
    let body: ActivateNowResponse | undefined;
    try {
      body = await AgenciesService.activateSubscriptionNow(agencyId);
    } catch (error) {
      body = bodyOf(error);
    } finally {
      inFlight.current = false;
    }
    const next = stateFromResponse(body);
    setState(next);
    return next;
  }, [agencyId]);

  const reset = useCallback(() => setState(CONFIRM), []);

  return { state, activate, reset };
}
